import axios, { AxiosHeaders, type AxiosRequestConfig } from 'axios';
import { fireUnauthorized, getAuthToken, hasAuthGetter } from './authToken';
import { resolvePdfTemplatesOrigin } from './pdfTemplatesApi';
import { ConflictError, HttpError } from './problemDetails';
import type { ProblemDetails } from './problemDetails';

const GLOBAL_KEY = '__RAPTOR_DOCUMENT_BUILDER_API_CLIENT__';
type ApiClient = ReturnType<typeof axios.create>;
const globalRecord = globalThis as unknown as Record<string, unknown>;
const existingClient = globalRecord[GLOBAL_KEY] as ApiClient | undefined;
const shouldSetupInterceptors = !existingClient;

export const apiClient: ApiClient =
  existingClient && typeof existingClient.request === 'function'
    ? existingClient
    : axios.create({
        baseURL: resolvePdfTemplatesOrigin(),
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

globalRecord[GLOBAL_KEY] = apiClient;

if (shouldSetupInterceptors) {
  apiClient.interceptors.request.use(async (config) => {
    if (!hasAuthGetter()) {
      // Not an exception: federation host might not have registered auth yet.
      console.warn('[document-builder] auth getter not registered; request will be unauthenticated');
    }

    const token = await getAuthToken();
    if (token) {
      const existingHeaders = config.headers as Record<string, unknown> | undefined;
      const nextHeaders = new AxiosHeaders((existingHeaders ?? {}) as Record<string, string>);
      nextHeaders.set('Authorization', `Bearer ${token}`);
      config.headers = nextHeaders;
    }

    return config;
  });

  apiClient.interceptors.response.use(
    (res) => res,
    (error) => {
      const status = error?.response?.status ?? 0;
      const problem: ProblemDetails = error?.response?.data ?? {};

      if (status === 401) fireUnauthorized();
      if (status === 403) {
        console.warn('[document-builder] 403: missing org context or insufficient permission');
      }
      if (status === 409) {
        return Promise.reject(new ConflictError(problem));
      }
      if (status >= 400) {
        return Promise.reject(new HttpError(status, problem));
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Orval mutator: return response body (not AxiosResponse).
 *
 * Orval may call this as either:
 * - `customInstance(config: AxiosRequestConfig)`
 * - `customInstance(url: string, options?: RequestInit)`
 */
export async function customInstance<T>(configOrUrl: AxiosRequestConfig | string, options?: RequestInit): Promise<T> {
  const axiosConfig: AxiosRequestConfig =
    typeof configOrUrl === 'string'
      ? {
          url: configOrUrl,
          method: options?.method,
          headers: options?.headers as AxiosRequestConfig['headers'],
          signal: options?.signal ?? undefined,
          data: (options as unknown as { body?: unknown }).body
        }
      : configOrUrl;

  const response = await apiClient.request<T>(axiosConfig);
  return response.data;
}
